import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Loader2, Key, CheckCircle2, AlertCircle, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [keySet, setKeySet] = useState(false);
  const [keyMasked, setKeyMasked] = useState("");
  const [keyInput, setKeyInput] = useState("");

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings`);
      setKeySet(res.data.user_openai_api_key_set);
      setKeyMasked(res.data.user_openai_api_key_masked);
    } catch {
      toast.error("Failed to load admin settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/settings`, {
        user_openai_api_key: keyInput.trim(),
      });
      setKeySet(res.data.user_openai_api_key_set);
      setKeyMasked(res.data.user_openai_api_key_masked);
      setKeyInput("");
      toast.success("OpenAI API key saved — AI generation now bills your OpenAI account directly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Clear your OpenAI key? AI generation will fall back to consuming Emergent credits."))
      return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, { user_openai_api_key: "" });
      setKeySet(false);
      setKeyMasked("");
      setKeyInput("");
      toast.info("Key cleared. AI generation now uses Emergent credits.");
    } catch {
      toast.error("Failed to clear");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="settings-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-800">Admin Settings</h1>
        <p className="text-slate-500 mt-1">
          Global configuration that applies to every project.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-orange-500" />
            OpenAI API key (own billing)
          </CardTitle>
          <CardDescription>
            Provide your own OpenAI API key to bypass Emergent credits — AI content generation
            (service pages, city pages, FAQs, SEO fixes, etc.) will route directly to OpenAI's
            API and bill your OpenAI account. Leave empty to use the default Emergent universal key.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm border ${
              keySet
                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                : "bg-amber-50 border-amber-200 text-amber-700"
            }`}
            data-testid="settings-key-status"
          >
            {keySet ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {keySet ? (
              <>
                Currently using your own key: <code className="font-mono">{keyMasked}</code>{" "}
                — Emergent credits not consumed.
              </>
            ) : (
              <>
                No key set — AI generation falls back to <strong>EMERGENT_LLM_KEY</strong> (consumes Emergent credits).
              </>
            )}
          </div>

          <div>
            <Label htmlFor="openai-key-input">
              {keySet ? "Replace key" : "Set key"}
            </Label>
            <Input
              id="openai-key-input"
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="sk-proj-..."
              autoComplete="off"
              spellCheck={false}
              data-testid="settings-openai-key-input"
              className="mt-1.5 font-mono text-sm"
            />
            <p className="text-xs text-slate-500 mt-1">
              The key is stored server-side and only its first / last characters are
              displayed back. Get one at{" "}
              <a
                href="https://platform.openai.com/api-keys"
                target="_blank"
                rel="noreferrer"
                className="text-orange-600 hover:underline"
              >
                platform.openai.com/api-keys
              </a>.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={handleSave}
              disabled={saving || !keyInput.trim()}
              data-testid="settings-save-key-btn"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {keySet ? "Replace key" : "Save key"}
            </Button>
            {keySet && (
              <Button
                variant="outline"
                onClick={handleClear}
                disabled={saving}
                data-testid="settings-clear-key-btn"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                Clear
              </Button>
            )}
          </div>

          <div className="text-xs text-slate-500 border-t pt-4 space-y-1">
            <p>
              <strong>Tip:</strong> on the OpenAI side, the requests will appear as
              "gpt-5.2" — the model used by every AI generation in this app.
            </p>
            <p>
              <strong>Cost reference:</strong> ~$0.15 / 1M input + $0.60 / 1M output tokens for GPT-5.2.
              A typical city/service page generation uses 2–5k tokens.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
